import { VersionRange } from '../../models/core/version-range.js';
import {
  cloneListOfCloneable, DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData,
} from '../../models/dto/dto.js';
import { Voucher } from '../../models/serializable/discounts/voucher.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Component } from '../core/component.js';

/**
 * @deprecated Use `OrderDiscountCodeComponent' instead.
 */
export class LegacyOrderVoucherComponent extends Component {
  static readonly TYPE_ID: string = 'b65713254e6e41a0aec400cf52b38650';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly vouchers: Voucher[],
  ) {
    super();
  }

  get $typeId(): string {
    return LegacyOrderVoucherComponent.TYPE_ID;
  }

  get $removable(): boolean {
    return false;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(LegacyOrderVoucherComponent.SCHEMA_VERSION);
  }

  static create(args: { vouchers: Voucher[] }): LegacyOrderVoucherComponent {
    return new LegacyOrderVoucherComponent(args.vouchers);
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyOrderVoucherComponent {
    return this.create({
      vouchers: MapUtils.getList<Voucher>(map, 'vouchers', (v) => context.fromData<Voucher>(v as SerializedData)),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'vouchers': this.vouchers.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return LegacyOrderVoucherComponent.create({
      vouchers: cloneListOfCloneable(this.vouchers),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameList(other.vouchers, this.vouchers, isSameData);
  }
}

export class LegacyOrderVoucherComponentBuilder extends DataModelBuilder<LegacyOrderVoucherComponent> {
  build(context: SerializationContext, map: SerializedData): LegacyOrderVoucherComponent {
    return LegacyOrderVoucherComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(LegacyOrderVoucherComponent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(LegacyOrderVoucherComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the [paymentRequestId] field was added.
    if(version < 2) {
      const voucher = context.fromData<Voucher>(MapUtils.getRecord(map, 'voucher'));
      map['vouchers'] = [context.toData(voucher)];

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  migrateFromCurrentSchemaVersion(
      context: SerializationContext,
      model: LegacyOrderVoucherComponent,
      map: SerializedData
  ): SerializedData {
    let version = model.$schemaVersion.to;
    if(version >= 2) {
      if(model.vouchers.length !== 1) return map;

      map['voucher'] = context.toData(model.vouchers[0]);
      delete map['vouchers'];

      map['$schema_version'] = VersionRange.encodedExactly(1);
      version = 1;
    }

    return map;
  }
}
